/**
 * Banner guest main screen register form (`main_screen_variant: banner_guest`).
 * Tune independently from classic / hero_dock sheet forms.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { View, Text, StyleSheet, Keyboard, Modal, Platform, Pressable } from 'react-native';
import { ButtonLoader } from '../../../components/load-main/ButtonLoader';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { setSelectedSubscription } from '../../../data/domains/subscription';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTranslation, layoutLtrStyle } from '../../../shared/i18n';
import { useRtl } from '../../../shared/i18n/useRtl';
import { resolveCmsPageLocalizedHtml, resolveCmsPageLocalizedTitle, findCmsPageByTitleOrMetaTitle } from '../../../data/domains/config';
import { useThemeColors, type ThemeColors, appTheme } from '../../../shared/theme';
import { ApiClient } from '../../../shared/api/client';
import { endpoints } from '../../../shared/config/endpoints';
import { postRegisterRequest, registerSuccessPersistToStore } from '../../../shared/auth';
import { getFcmDeviceTokenForAuth } from '../../../platform/notifications/fcmDeviceToken';
import type { CmsResponse } from '../../../data/domains/config';
import { useOrganizationSettings, isHasLastNameEnabled, resolvePhoneMaxDigits } from '../../../data/domains/config';
import { useAreas } from '../../../data/domains/addresses';
import { Clickable } from '../../../components/common/Clickable';
import { Input } from '../../../components/common/Input';
import { AppPickerTrigger } from '../../../components/common/AppPickerTrigger';
import { AreaPickerModal } from '../../../components/common/AreaPickerModal';
import { SvgIcon } from '../../../components/common/SvgIcon';
import { ErrorState } from '../../../components/common/ErrorState';
import { useAsyncWithLoading } from '../../../components/ui/hooks/useAsyncWithLoading';
import { cmsPageHtmlToDisplayText } from '../../../shared/utils/htmlToPlainText';
import { CmsPagesApiHtmlScroll } from '../../../components/common/CmsPagesApiHtmlScroll';
import { formFieldHalf, formFieldRow } from '../../../shared/theme/styles/forms';

export interface OTPNavigateParams {
  phone: string;
  countryCode: string;
  context: string;
  redirectTo: string;
  signupPassword: string;
  customer_id: number | undefined;
}

export type SignUpFormFields = {
  name: string;
  last_name: string;
  email: string;
  phone: string;
  password: string;
  areasOrRegionId: string;
  referral_code: string;
  country_iso_code: string;
  device_type: string;
  device_token: string;
};

/** Fired from `RegisterSheetContent` (auth bottom sheet) — v1 session + `openOtp` flow; inner form is `SignUpCredentialForm`. */
export type AuthSheetRegisterCompleteDetail = {
  response: { success?: boolean; data?: { customer?: unknown; session_token?: string }; customer?: unknown; message?: string; session_token?: string };
  form_data: SignUpFormFields;
  subData?: unknown;
  dialing_code_str: string;
};

interface SignUpCredentialFormProps {
  onClose: () => void;
  onRegisterSuccess: (params: OTPNavigateParams) => void;
  onOpenSignIn: () => void;
  /** Guest / checkout — forwarded to the auth-sheet register handler. */
  subData?: unknown;
  /**
   * When set, successful register does **not** run the standalone v2 `onRegisterSuccess` path; caller handles session + `openOtp` (e.g. `RegisterSheetContent` mirroring v1).
   */
  onAuthSheetRegisterComplete?: (detail: AuthSheetRegisterCompleteDetail) => void;
}

const FIELD_SPACING = 24;
const BUTTON_HEIGHT = 52;

function createSignUpSheetStyles(c: ThemeColors) {
  return StyleSheet.create({
    formSurface: {
      borderTopLeftRadius: appTheme.borderRadius.lg,
      borderTopRightRadius: appTheme.borderRadius.lg,
      overflow: 'hidden',
      backgroundColor: c.surface,
    },
    content: {
      paddingBottom: appTheme.spacing.xl,
      paddingTop: appTheme.spacing.xl,
    },
    errorContainer: {
      paddingVertical: appTheme.spacing.lg,
    },
    fieldGroup: {
      marginBottom: FIELD_SPACING,
    },
    inputContainer: {
      marginBottom: 0,
    },
    phoneRowLtr: {
      ...layoutLtrStyle,
      alignSelf: 'stretch',
      width: '100%',
    },
    phonePrefixWrap: {
      justifyContent: 'center',
      paddingRight: appTheme.spacing.sm,
      borderRightWidth: StyleSheet.hairlineWidth,
    },
    phonePrefixText: {
      fontSize: 16,
      lineHeight: 22,
      fontWeight: '600',
    },
    termsRowWrap: {
      flexDirection: 'row',
      alignItems: 'center',
    },
    checkbox: {
      width: 24,
      height: 24,
      borderWidth: 2,
      borderColor: c.border,
      borderRadius: 6,
      justifyContent: 'center',
      alignItems: 'center',
      marginEnd: appTheme.spacing.sm,
    },
    termsText: {
      fontSize: 12,
      lineHeight: 16,
      fontWeight: '400',
      flex: 1,
      flexShrink: 1,
      color: c.tertiary,
    },
    primaryButtonWrap: {
      marginTop: FIELD_SPACING,
      marginBottom: FIELD_SPACING,
    },
    primaryButton: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      height: BUTTON_HEIGHT,
      borderRadius: appTheme.borderRadius.md,
    },
    primaryButtonText: {
      ...appTheme.typography.body,
      fontWeight: '700',
      color: c.onPrimary,
    },
    footerRow: {
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: 4,
      paddingTop: FIELD_SPACING,
      minHeight: 44,
    },
    footerHint: {
      ...appTheme.typography.bodySmall,
    },
    footerLink: {
      ...appTheme.typography.bodyMedium,
    },
    termsModalBackdrop: {
      flex: 1,
      justifyContent: 'flex-end',
    },
    termsModalBox: {
      borderTopLeftRadius: 20,
      borderTopRightRadius: 20,
      maxHeight: '80%',
      zIndex: 1,
    },
    termsModalHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: appTheme.spacing.md,
      borderBottomWidth: 1,
    },
    termsModalTitle: {
      ...appTheme.typography.h3,
      flex: 1,
    },
    termsModalCloseBtn: {
      padding: appTheme.spacing.sm,
      minWidth: 44,
      minHeight: 44,
      justifyContent: 'center',
    },
    termsModalScrollWrap: {
      height: 360,
    },
    termsModalScroll: {
      flex: 1,
    },
    termsModalScrollContent: {
      paddingHorizontal: appTheme.spacing.md,
      paddingBottom: appTheme.spacing.md,
    },
    termsModalLoadingWrap: {
      height: 360,
      padding: appTheme.spacing.md,
      justifyContent: 'center',
      alignItems: 'center',
      gap: appTheme.spacing.md,
    },
    termsModalBody: {
      ...appTheme.typography.bodySmall,
      lineHeight: 22,
    },
    termsModalFooter: {
      flexDirection: 'row',
      paddingHorizontal: appTheme.spacing.md,
      paddingTop: appTheme.spacing.md,
      gap: appTheme.spacing.md,
      borderTopWidth: 1,
    },
    termsModalBtnWrap: {
      flex: 1,
    },
    termsModalDeclineBtn: {
      paddingVertical: 14,
      borderRadius: appTheme.borderRadius.md,
      alignItems: 'center',
    },
    termsModalDeclineText: {
      ...appTheme.typography.bodyMedium,
    },
    termsModalAcceptBtn: {
      paddingVertical: 14,
      borderRadius: appTheme.borderRadius.md,
      alignItems: 'center',
    },
    termsModalAcceptText: {
      ...appTheme.typography.bodyMedium,
    },
  });
}

/** Used by `RegisterSheetContent` and v2 `MainScreen` inline auth sheet. */
export function SignUpCredentialForm({
  onClose,
  onRegisterSuccess,
  onOpenSignIn,
  subData: subDataProp,
  onAuthSheetRegisterComplete,
}: SignUpCredentialFormProps): React.ReactElement | null {
  const { t, locale } = useTranslation();
  const rtl = useRtl();
  const insets = useSafeAreaInsets();
  const c = useThemeColors() ?? appTheme.colors;
  const styles = useMemo(() => createSignUpSheetStyles(c), [c]);
  const has_last_name_enabled = isHasLastNameEnabled();
  const { settings } = useOrganizationSettings();
  const show_referral = settings.showReferralField === true || (settings.showReferralField as unknown) === 'Yes';
  const raw_dialing = settings.dialing_code;
  const dialing_code_str = raw_dialing != null && String(raw_dialing).trim() !== '' ? String(raw_dialing).trim() : '965';
  const user_phone_allowed_digit = resolvePhoneMaxDigits(settings.allowedDigitForPhone, dialing_code_str);
  const country_iso_from_settings = typeof settings.country_iso_code === 'string' ? settings.country_iso_code : 'KW';

  const [form_data, setFormData] = useState({
    name: '',
    last_name: '',
    email: '',
    phone: '',
    password: '',
    areasOrRegionId: '',
    referral_code: '',
    country_iso_code: country_iso_from_settings,
    device_type: Platform.OS,
    device_token: '',
  });
  const [auth_error, setAuthError] = useState<string | null>(null);
  const [tc_checked, setTcChecked] = useState(false);
  const [loading, runAsync] = useAsyncWithLoading();
  const [selected_area_name, setSelectedAreaName] = useState('');
  const [show_area_picker, setShowAreaPicker] = useState(false);
  const [show_terms_modal, setShowTermsModal] = useState(false);
  const [terms_content, setTermsContent] = useState('');
  const [terms_title, setTermsTitle] = useState('');
  const [errors, setErrors] = useState<Record<string, boolean>>({});

  const { data: areas, isLoading: areas_loading, error: areas_error, refetch: refetch_areas } = useAreas();

  const loadTermsContent = useCallback(async () => {
    try {
      const response = await ApiClient.get<CmsResponse>(endpoints.cms);
      const pages = response?.data?.pages ?? response?.pages ?? [];
      const list = Array.isArray(pages) ? pages : [];
      const app_language = locale === 'ar' ? 'ar' : 'en';
      const tc_page = findCmsPageByTitleOrMetaTitle(list, 'Terms & Conditions', 'terms');
      if (tc_page) {
        const raw = resolveCmsPageLocalizedHtml(tc_page, app_language);
        setTermsContent(raw != null && String(raw).trim() !== '' ? String(raw) : '');
        setTermsTitle(resolveCmsPageLocalizedTitle(tc_page, app_language));
      } else {
        setTermsContent('');
        setTermsTitle('');
      }
    } catch (_) {
      setTermsContent('');
      setTermsTitle('');
    }
  }, [locale]);

  useEffect(() => {
    loadTermsContent();
  }, [loadTermsContent]);

  /** Refetch when the sheet opens so terms are loaded even if the first request failed or cache was empty. */
  useEffect(() => {
    if (show_terms_modal) {
      loadTermsContent();
    }
  }, [show_terms_modal, loadTermsContent]);

  const open_terms_modal = () => setShowTermsModal(true);

  const accept_terms = () => {
    setTcChecked(true);
    setShowTermsModal(false);
    setErrors(prev => ({ ...prev, tc: false }));
  };

  const update_field = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: false }));
  };

  const validate_form = (): boolean => {
    const new_errors: Record<string, boolean> = {};
    if (!form_data.name || form_data.name.trim().length < 2) new_errors.name = true;
    if (has_last_name_enabled && (!form_data.last_name || form_data.last_name.trim().length < 2)) new_errors.last_name = true;
    if (!form_data.email || form_data.email.trim().length < 5) new_errors.email = true;
    if (!form_data.phone || form_data.phone.trim().length < 6) new_errors.phone = true;
    if (!form_data.password || form_data.password.trim().length < 6) new_errors.password = true;
    if (!form_data.areasOrRegionId) new_errors.area = true;
    if (!tc_checked) new_errors.tc = true;
    setErrors(new_errors);
    return Object.keys(new_errors).length === 0;
  };

  const handle_register = async () => {
    if (!validate_form()) {
      setAuthError(t('auth.fillAllFieldsCorrectly'));
      return;
    }
    setAuthError(null);
    await runAsync(async () => {
      const device_token = await getFcmDeviceTokenForAuth();
      const payload = {
        ...form_data,
        last_name: has_last_name_enabled ? form_data.last_name : form_data.name,
        device_token,
        TcChecked: tc_checked,
        area_id: form_data.areasOrRegionId ? Number(form_data.areasOrRegionId) : undefined,
      };
      const response = (await postRegisterRequest(payload)) as {
        success?: boolean;
        message?: string;
        data?: { customer?: unknown };
        customer?: unknown;
      };

      if (response.success === true) {
        if (onAuthSheetRegisterComplete) {
          onAuthSheetRegisterComplete({
            response: response as AuthSheetRegisterCompleteDetail['response'],
            form_data: { ...form_data },
            subData: subDataProp,
            dialing_code_str,
          });
          return;
        }
        const customer_data = response.data?.customer ?? response.customer;
        if (!customer_data) {
          return;
        }
        const persist = await registerSuccessPersistToStore(response);
        if (!persist.ok) {
          setAuthError(persist.message || t('auth.registrationFailed'));
          return;
        }
        const customer_id = (persist.user as { id?: number }).id;
        onClose();
        onRegisterSuccess({
          phone: form_data.phone,
          countryCode: `+${dialing_code_str}`,
          context: 'signup',
          redirectTo: 'MainTabs',
          signupPassword: form_data.password,
          customer_id,
        });
      } else {
        setAuthError(response.message || t('auth.registrationFailed'));
      }
    }).catch((error: unknown) => {
      setAuthError(error instanceof Error ? error.message : t('auth.unexpectedError'));
    });
  };

  const select_area = (area: { id: number; name?: string; title?: string }) => {
    Keyboard.dismiss();
    update_field('areasOrRegionId', String(area.id));
    setSelectedAreaName(area.title ?? area.name ?? '');
    setErrors(prev => ({ ...prev, area: false }));
    setShowAreaPicker(false);
  };

  const accent = c.primary;

  if (auth_error) {
    return (
      <View style={styles.formSurface}>
        <View style={styles.errorContainer}>
        <ErrorState
          title={t('common.error')}
          message={auth_error}
          onRetry={() => setAuthError(null)}
          issue_context={{ page: 'Sign up', issue: 'Registration failed', kind: 'mutation' }}
        />
        </View>
      </View>
    );
  }

  return (
    <View style={styles.formSurface}>
      <View style={styles.content}>
        <View style={styles.fieldGroup}>
          <View style={has_last_name_enabled ? formFieldRow : undefined}>
            <Input
              value={form_data.name}
              onChangeText={v => update_field('name', v)}
              placeholder={t('auth.enterFirstName')}
              error={errors.name ? t('auth.required') : undefined}
              containerStyle={[styles.inputContainer, has_last_name_enabled ? formFieldHalf : undefined]}
            />
            {has_last_name_enabled ? (
              <Input
                value={form_data.last_name}
                onChangeText={v => update_field('last_name', v)}
                placeholder={t('auth.enterLastName')}
                error={errors.last_name ? t('auth.required') : undefined}
                containerStyle={[styles.inputContainer, formFieldHalf]}
              />
            ) : null}
          </View>
        </View>
        <View style={styles.fieldGroup}>
          <Input
            value={form_data.email}
            onChangeText={v => update_field('email', v)}
            placeholder={t('auth.enterEmail')}
            keyboardType="email-address"
            autoCapitalize="none"
            error={errors.email ? t('auth.required') : undefined}
            containerStyle={styles.inputContainer}
          />
        </View>
        <View style={styles.fieldGroup}>
          <View style={styles.phoneRowLtr}>
            <Input
              label={t('auth.phoneNumber')}
              value={form_data.phone}
              onChangeText={v => update_field('phone', v.replace(/\D/g, '').slice(0, user_phone_allowed_digit))}
              placeholder={t('auth.phonePlaceholder')}
              keyboardType="phone-pad"
              maxLength={user_phone_allowed_digit}
              error={errors.phone ? t('auth.required') : undefined}
              containerStyle={styles.inputContainer}
              textAlign="left"
              leftAccessory={
                <View style={[styles.phonePrefixWrap, { borderRightColor: c.borderLight }]}>
                  <Text
                    style={[styles.phonePrefixText, { color: c.text }]}
                    {...(Platform.OS === 'android' ? { includeFontPadding: false } : {})}>
                    +{dialing_code_str}
                  </Text>
                </View>
              }
            />
          </View>
        </View>
        <View style={styles.fieldGroup}>
          <Input
            value={form_data.password}
            onChangeText={v => update_field('password', v)}
            placeholder={t('auth.enterPassword')}
            secureTextEntry
            error={errors.password ? t('auth.required') : undefined}
            containerStyle={styles.inputContainer}
          />
        </View>
        <View style={styles.fieldGroup}>
          <AppPickerTrigger
            value={selected_area_name || null}
            placeholder={
              areas_loading ? t('common.loading') : areas_error && (areas?.length ?? 0) === 0 ? t('common.retry') : t('auth.selectArea')
            }
            onPress={() => (areas_error && (areas?.length ?? 0) === 0 ? refetch_areas() : setShowAreaPicker(true))}
            disabled={areas_loading}
            loading={areas_loading}
            error={errors.area ? t('auth.required') : undefined}
            accessibilityLabel={t('auth.selectArea')}
            containerStyle={styles.inputContainer}
          />
        </View>

        <AreaPickerModal
          visible={show_area_picker}
          areas={areas ?? []}
          onSelect={select_area}
          onClose={() => setShowAreaPicker(false)}
          selectedAreaId={form_data.areasOrRegionId}
        />

        {show_referral && (
          <View style={styles.fieldGroup}>
            <Input
              value={form_data.referral_code}
              onChangeText={v => update_field('referral_code', v)}
              placeholder={t('auth.enterReferralCode')}
              autoCapitalize="characters"
              containerStyle={styles.inputContainer}
            />
          </View>
        )}

        <Clickable useNativePressable testID="create-account-terms-agree" onPress={open_terms_modal} accessibilityLabel={t('auth.agreeToTermsAndConditions')} accessibilityRole="button">
          <View style={styles.termsRowWrap}>
            <View style={[styles.checkbox, tc_checked && { backgroundColor: accent, borderColor: accent }, errors.tc && { borderColor: c.error }]}>
              {tc_checked && <SvgIcon name="check" size={16} color={c.onPrimary} />}
            </View>
            <Text style={[styles.termsText, { color: c.textSecondary }]} numberOfLines={1} adjustsFontSizeToFit minimumFontScale={0.85}>
              {t('auth.agreeToTermsAndConditions')}
            </Text>
          </View>
        </Clickable>

        <Clickable useNativePressable testID="create-account-continue" onPress={handle_register} disabled={loading} style={styles.primaryButtonWrap} accessibilityLabel={t('auth.continue')} accessibilityRole="button">
          <View style={[styles.primaryButton, { backgroundColor: accent }]}>
            {loading ? <ButtonLoader size={18} color={c.onPrimary} /> : <Text style={styles.primaryButtonText}>{t('auth.continue')}</Text>}
          </View>
        </Clickable>

        <View style={styles.footerRow}>
          <Text style={[styles.footerHint, { color: c.textSecondary }]}>{t('auth.alreadyHaveAccount')}</Text>
          <Clickable useNativePressable testID="create-account-signin" onPress={onOpenSignIn} accessibilityLabel={t('auth.signIn')} accessibilityRole="button">
            <Text style={[styles.footerLink, { color: accent }]}>{t('auth.signIn')}</Text>
          </Clickable>
        </View>
      </View>

      <Modal visible={show_terms_modal} animationType="slide" transparent>
        <GestureHandlerRootView style={{ flex: 1 }} {...(Platform.OS === 'android' ? { unstable_forceActive: true } : {})}>
        <View style={[styles.termsModalBackdrop, { backgroundColor: appTheme.overlay }]}>
          <Pressable style={StyleSheet.absoluteFill} onPress={() => setShowTermsModal(false)} accessibilityRole="button" accessibilityLabel={t('common.close')} />
          <View style={[styles.termsModalBox, { backgroundColor: c.surface }]}>
            <View style={[styles.termsModalHeader, { borderBottomColor: c.border }]}>
              <Text style={[styles.termsModalTitle, { color: c.text }]}>
                {terms_title || t('auth.termsAndConditionsTitle')}
              </Text>
              <Clickable useNativePressable testID="create-account-close" onPress={() => setShowTermsModal(false)} accessibilityLabel={t('common.close')} accessibilityRole="button">
                <View style={styles.termsModalCloseBtn}>
                  <SvgIcon name="close" size={24} color={c.text} />
                </View>
              </Clickable>
            </View>
            <View style={styles.termsModalScrollWrap}>
              {cmsPageHtmlToDisplayText(terms_content) ? (
                <CmsPagesApiHtmlScroll
                  html={terms_content}
                  scrollViewStyle={styles.termsModalScroll}
                  contentContainerStyle={styles.termsModalScrollContent}
                  textStyle={[styles.termsModalBody, rtl.localizedTextStyle, { color: c.textSecondary }]}
                />
              ) : (
                <View style={styles.termsModalLoadingWrap}>
                  <ButtonLoader size={20} color={c.primary} />
                  <Text style={[styles.termsModalBody, rtl.localizedTextStyle, { color: c.textSecondary }]}>{t('auth.loadingTerms')}</Text>
                </View>
              )}
            </View>
            <View style={[styles.termsModalFooter, { borderTopColor: c.border, paddingBottom: appTheme.spacing.lg + insets.bottom }]}>
              <Clickable useNativePressable onPress={() => setShowTermsModal(false)} style={styles.termsModalBtnWrap}>
                <View style={[styles.termsModalDeclineBtn, { backgroundColor: c.surfaceLight }]}>
                  <Text style={[styles.termsModalDeclineText, { color: c.text }]}>{t('auth.decline')}</Text>
                </View>
              </Clickable>
              <Clickable useNativePressable testID="create-account-terms-agree" onPress={accept_terms} style={styles.termsModalBtnWrap}>
                <View style={[styles.termsModalAcceptBtn, { backgroundColor: c.primary }]}>
                  <Text style={[styles.termsModalAcceptText, { color: c.onPrimary }]}>{t('auth.accept')}</Text>
                </View>
              </Clickable>
            </View>
          </View>
        </View>
        </GestureHandlerRootView>
      </Modal>
    </View>
  );
}
